import type React from "react";
import { Box, Small, P, Flex, FlexItem } from "orcs-design-system";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";

import { RichTextContentViewer } from "src/shared/RichTextContent/RichTextContent";
import { isPlainLeftClick, TreeRowLinkLabel } from "src/shared/TreeViewNext";
import type { TagNavNode } from "./index.logic";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComp = any;

const TagTreeHeaderTitleRouterLink = styled(Link as AnyComp)`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: none;
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  min-width: 0;
  flex: 1;
  overflow: hidden;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

const TagTreeHeaderTitleLink = styled.a`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: none;
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  min-width: 0;
  flex: 1;
  overflow: hidden;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

const TreeText = styled(P as AnyComp)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  z-index: 2;
  transition: ${themeGet("transition.transitionDefault")};
`;

type TagTreeHeaderProps = {
  node: TagNavNode;
  onActivate?: () => void;
  getTagLinkTo?: (node: TagNavNode) => string | null;
  /** When false (Explore), parent onNodeSelected owns SPA navigation. */
  navigateTagTypeWithHistory?: boolean;
};

const TagTreeHeader = ({
  node,
  onActivate,
  getTagLinkTo,
  navigateTagTypeWithHistory = true,
}: TagTreeHeaderProps) => {
  const history = useHistory();
  const { isTagType, isTagTypeDesc, name, tagValue } = node;
  const isBranchOpen = node.toggled === true;

  // Tags sidebar vs Explore share real hrefs for copy-link, but plain-click routing
  // differs: never call full onActivate on an already-open branch (it collapses and,
  // with suppressOnNodeSelectedWhenCollapsingBranch, skips navigation).
  const handlePlainLinkClick = (
    e: React.MouseEvent,
    href: string,
    { routerLink = false }: { routerLink?: boolean } = {}
  ) => {
    if (!isPlainLeftClick(e)) {
      return;
    }

    e.stopPropagation();

    if (routerLink) {
      if (!isBranchOpen) {
        onActivate?.();
      }
      return;
    }

    e.preventDefault();

    if (isBranchOpen) {
      history.push(href);
      return;
    }

    onActivate?.();
  };

  if (isTagType) {
    const tagTypeHref = getTagLinkTo?.(node) ?? null;
    const tagTypeTitle = tagTypeHref ? (
      <TreeRowLinkLabel
        data-testid="treeheader-node-name"
        data-tree-row-content
        title={name}
      >
        {name}
      </TreeRowLinkLabel>
    ) : (
      <TreeText fontSize="1" data-testid="treeheader-node-name" title={name}>
        {name}
      </TreeText>
    );

    if (tagTypeHref) {
      if (navigateTagTypeWithHistory) {
        return (
          <TagTreeHeaderTitleRouterLink
            className="treeHeaderTitle"
            data-tree-row-action
            to={tagTypeHref}
            onClick={(e: React.MouseEvent) =>
              handlePlainLinkClick(e, tagTypeHref, { routerLink: true })
            }
            style={{ minWidth: 0 }}
          >
            {tagTypeTitle}
          </TagTreeHeaderTitleRouterLink>
        );
      }

      return (
        <TagTreeHeaderTitleLink
          className="treeHeaderTitle"
          data-tree-row-action
          href={tagTypeHref}
          onClick={(e) => handlePlainLinkClick(e, tagTypeHref)}
          style={{ minWidth: 0 }}
        >
          {tagTypeTitle}
        </TagTreeHeaderTitleLink>
      );
    }

    return tagTypeTitle;
  }

  if (isTagTypeDesc) {
    const RTV = RichTextContentViewer as AnyComp;
    return (
      <Box data-testid="tag-type-desc-node" style={{ pointerEvents: "none" }}>
        <Small color="greyDark" style={{ display: "block" }}>
          <RTV content={node.details} fontSize="1.2rem" />
        </Small>
      </Box>
    );
  }

  const tagVal = tagValue as Record<string, unknown> | null | undefined;
  const appliedCount = tagVal?.["appliedCount"];

  const tagHref = getTagLinkTo?.(node) ?? null;

  const tagValueTitle = (
    <>
      <TreeText fontSize="1" data-testid="treeheader-node-name" title={name}>
        {name}
      </TreeText>
      {appliedCount ? (
        <FlexItem flex="0 0 auto">
          <Small color="subText">{`${appliedCount} tagged`}</Small>
        </FlexItem>
      ) : null}
    </>
  );

  if (tagHref) {
    return (
      <Flex alignItems="center" style={{ minWidth: 0, flex: 1 }}>
        <TagTreeHeaderTitleLink
          className="treeHeaderTitle"
          data-tree-row-action
          href={tagHref}
          onClick={(e) => handlePlainLinkClick(e, tagHref)}
          style={{ minWidth: 0, flex: 1 }}
        >
          <TreeRowLinkLabel
            data-testid="treeheader-node-name"
            data-tree-row-content
            title={name}
          >
            {name}
          </TreeRowLinkLabel>
        </TagTreeHeaderTitleLink>
        {appliedCount ? (
          <FlexItem flex="0 0 auto">
            <Small color="subText">{`${appliedCount} tagged`}</Small>
          </FlexItem>
        ) : null}
      </Flex>
    );
  }

  return <>{tagValueTitle}</>;
};

export default TagTreeHeader;
